import type { BigNumber } from '@ethersproject/bignumber';

import { addBignumbers, bn, divideBignumbers, multiplyBignumbers } from 'tools/math';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import type { IAccountWithBalance, IAggregatedBalancesBN } from '../model/types';

const DECIMALS = 18;
const ZERO = bn('0');

/**
 * Aggregates balance data from multiple accounts
 * Returns BigNumber objects for proper formatting with formatToken(value, 18)
 *
 * @param accounts - Array of accounts with balance data
 * @returns Aggregated balances as BigNumber objects with locked percentages
 */
export function aggregateBalances(accounts: IAccountWithBalance[]): IAggregatedBalancesBN {
  if (accounts.length === 0) {
    return {
      actualBase: ZERO,
      actualBaseLocked: ZERO,
      actualQuote: ZERO,
      actualQuoteLocked: ZERO,
      lockedBasePercent: ZERO,
      lockedQuotePercent: ZERO,
    };
  }

  const reduced = accounts.reduce(
    (acc, account) => ({
      actualBase: addBignumbers(
        [acc.actualBase, DECIMALS],
        [bn(normalizeNumber(account.base_balance ?? '0'), DECIMALS), DECIMALS],
      ),
      actualBaseLocked: addBignumbers(
        [acc.actualBaseLocked, DECIMALS],
        [bn(normalizeNumber(account.base_balance_locked ?? '0'), DECIMALS), DECIMALS],
      ),
      actualQuote: addBignumbers(
        [acc.actualQuote, DECIMALS],
        [bn(normalizeNumber(account.quote_balance ?? '0'), DECIMALS), DECIMALS],
      ),
      actualQuoteLocked: addBignumbers(
        [acc.actualQuoteLocked, DECIMALS],
        [bn(normalizeNumber(account.quote_balance_locked ?? '0'), DECIMALS), DECIMALS],
      ),
    }),
    {
      actualBase: ZERO,
      actualBaseLocked: ZERO,
      actualQuote: ZERO,
      actualQuoteLocked: ZERO,
    } as {
      actualBase: BigNumber;
      actualBaseLocked: BigNumber;
      actualQuote: BigNumber;
      actualQuoteLocked: BigNumber;
    },
  );

  // Calculate locked percentages
  const lockedBasePercent = reduced.actualBase.eq(0)
    ? ZERO
    : multiplyBignumbers(
        [
          divideBignumbers([reduced.actualBaseLocked, DECIMALS], [reduced.actualBase, DECIMALS]),
          DECIMALS,
        ],
        [bn(100), DECIMALS],
      );

  const lockedQuotePercent = reduced.actualQuote.eq(0)
    ? ZERO
    : multiplyBignumbers(
        [
          divideBignumbers([reduced.actualQuoteLocked, DECIMALS], [reduced.actualQuote, DECIMALS]),
          DECIMALS,
        ],
        [bn(100), DECIMALS],
      );

  return {
    actualBase: reduced.actualBase,
    actualBaseLocked: reduced.actualBaseLocked,
    actualQuote: reduced.actualQuote,
    actualQuoteLocked: reduced.actualQuoteLocked,
    lockedBasePercent,
    lockedQuotePercent,
  };
}

/**
 * Filters accounts by selected account IDs
 * If selectedIds is empty, returns all accounts
 *
 * @param accounts - All accounts
 * @param selectedIds - Selected account IDs (empty means all)
 * @returns Filtered accounts
 */
export function filterAccountsBySelection<T extends { id: number }>(
  accounts: T[],
  selectedIds: number[],
): T[] {
  if (selectedIds.length === 0) {
    return accounts;
  }
  return accounts.filter(acc => selectedIds.includes(acc.id));
}

/**
 * Converts balance string to formatted display value
 */
export function formatBalance(value: string, decimals: number = 2): string {
  const num = parseFloat(value);

  if (isNaN(num) || !isFinite(num)) {
    return '0';
  }

  const abs = Math.abs(num);

  // For very large numbers, use abbreviated format
  if (abs >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(2) + 'B';
  }
  if (abs >= 1_000_000) {
    return (num / 1_000_000).toFixed(2) + 'M';
  }
  if (abs >= 1_000) {
    return (num / 1_000).toFixed(2) + 'K';
  }

  return num.toFixed(decimals);
}
